import { useEffect, useState } from 'react';

import { AppShell } from '@mantine/core';

import { useAuth } from '../../contexts/AuthContext';
import { AgentChat } from './AgentChat';
import { AgentResizeHandle } from './AgentResizeHandle';
import type { AgentPanelWidth } from './agentPanel';

/**
 * Above the header: the panel runs the full height of the screen, and on a
 * phone it covers the header as it covers the rest of the page.
 */
const AGENT_PANEL_Z_INDEX = 201;

type AgentDockProps = {
  opened: boolean;
  onMinimize: () => void;
  panelWidth: AgentPanelWidth;
};

/**
 * The agent, on every page: a panel down the right side of the screen. The
 * page moves left to make room for it, so it covers nothing.
 *
 * The chat stays mounted once opened, and minimizing only collapses the
 * panel. A turn can take most of a minute, and the question in flight must
 * still be there when the person opens the panel again.
 */
export function AgentDock({ opened, onMinimize, panelWidth }: AgentDockProps) {
  const { user } = useAuth();
  const [everOpened, setEverOpened] = useState(opened);
  useEffect(() => {
    if (opened) setEverOpened(true);
  }, [opened]);

  return (
    <AppShell.Aside
      inert={!opened}
      style={{ top: 0, height: '100dvh', zIndex: AGENT_PANEL_Z_INDEX }}
    >
      <AgentResizeHandle {...panelWidth} />
      {/* Keyed by the person: after a sign-in or an impersonation, nothing of the
          last person's chat, such as a question in flight, carries over. */}
      {user && everOpened && (
        <AgentChat key={user.displayId} firstName={user.firstName} onMinimize={onMinimize} />
      )}
    </AppShell.Aside>
  );
}
